import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * Buttons as the Timesheet Hub MFE renders them: 9px corner radius (not a
 * full pill), Open Sans semibold. Primary = navy/white; outline & secondary =
 * white with the toolbar-chip border and dark-grey text (the "Filters" chip);
 * ghost = tertiary text token.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[9px] text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:bg-[#e8e8e8] disabled:text-[#969696] [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[var(--btn-primary-bg)] text-white hover:bg-[var(--btn-primary-hover)]",
        destructive: "bg-[var(--gw-text-error)] text-white hover:opacity-90",
        outline:
          "border border-[var(--th-chip-border)] bg-white text-[var(--th-chip-text)] hover:bg-[var(--th-meta-bg)]",
        secondary:
          "border border-[var(--th-chip-border)] bg-white text-[var(--th-chip-text)] hover:bg-[var(--th-meta-bg)]",
        ghost: "text-[var(--btn-tertiary-text)] hover:bg-accent",
        link: "text-[var(--gw-text-link)] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-5 py-2 text-sm font-semibold",
        sm: "h-8 px-4 text-sm font-medium",
        lg: "h-11 px-8 text-base font-semibold",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
